import React, { useEffect, useRef, useState } from "react";
import {
  X, Check, Ban, RotateCcw, MapPin, Mail, Phone, Receipt, FileText, CalendarClock, Clock, Wrench,
  UserSquare2, ExternalLink, AlertTriangle, CheckCircle2, XCircle, Send, ShieldCheck, Building2,
} from "lucide-react";
import { useDialog, DialogFrame, DialogSection } from "../Admindashboard/ui";
import { FONT_HEADING, formatDate, fromNow, Field } from "../userManagement/shared";
import { BusinessAvatar } from "../userManagement/ServiceProvider";
import { AppStatusBadge } from "./ApplicationCard";
import { REJECT_REASONS, isLicenseExpired, licenseExpiresSoon } from "./applicationsStore";

/**
 * Review dialog for a service-provider application.
 * mode "reject" opens straight on the rejection form (used by the card's ✕ button).
 */
const DocumentModal = ({ data, mode = "view", onClose, onApprove, onReject }) => {
  const closeRef = useRef(null);
  const [docIndex, setDocIndex] = useState(0);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");

  useDialog(!!data, onClose);
  useEffect(() => {
    if (!data) return;
    setDocIndex(0);
    setRejecting(mode === "reject");
    setReason("");
    closeRef.current?.focus();
  }, [data, mode]);

  if (!data) return null;

  const status = String(data.status || "pending").toLowerCase();
  const expired = isLicenseExpired(data);
  const expiringSoon = licenseExpiresSoon(data);
  const doc = data.documents?.[docIndex];

  const submitReject = () => {
    const r = reason.trim();
    if (!r) return;
    onReject(data.id, r);
  };

  return (
    <DialogFrame labelledBy="application-title" onClose={onClose} maxWidth={1000}>
      {/* Header */}
      <div className="relative shrink-0 px-6 pt-6 pb-5 sm:px-4 text-[#fff] overflow-hidden" style={{ background: "linear-gradient(120deg, #1B1B22 0%, #2A1614 60%, #EA352B 140%)" }}>
        <div className="pointer-events-none absolute -right-12 -top-16 w-56 h-56 rounded-full bg-[#EA352B] opacity-20" />
        <button
          ref={closeRef}
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute z-10 top-4 right-4 w-9 h-9 rounded-xl bg-[#ffffff1f] hover:bg-[#ffffff33] flex items-center justify-center transition"
        >
          <X size={18} className="text-[#fff]" />
        </button>
        <div className="relative flex items-center gap-4 pr-10">
          <span className="rounded-2xl p-1 bg-[#ffffff1f] shrink-0"><BusinessAvatar name={data.title} size={56} /></span>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <AppStatusBadge status={status} />
              <span className="text-[11px] text-[#ffffff99]">Application #{String(data.id).padStart(4, "0")}</span>
            </div>
            <h2 id="application-title" className="m-0 mt-1.5 text-2xl sm:text-xl font-medium truncate" style={FONT_HEADING}>{data.title}</h2>
            <p className="m-0 mt-0.5 text-xs text-[#ffffffb3] flex items-center gap-1.5 min-w-0">
              <MapPin size={12} className="shrink-0" /> <span className="truncate">{data.location}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-y-auto p-6 sm:p-4 grid grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] md:grid-cols-1 gap-5">
        <div className="space-y-4 min-w-0">
          {status === "rejected" && data.rejectReason && (
            <div className="flex gap-3 rounded-2xl bg-[#FFF5F4] border border-[#F6C9C5] p-4">
              <XCircle size={18} className="text-[#EA352B] shrink-0 mt-0.5" />
              <div>
                <p className="m-0 text-sm font-semibold text-[#991B1B]">Rejected {fromNow(data.reviewedAt)}</p>
                <p className="m-0 mt-0.5 text-sm text-[#B91C1C]">{data.rejectReason}</p>
              </div>
            </div>
          )}
          {status === "pending" && (expired || expiringSoon) && (
            <div className={`flex gap-3 rounded-2xl p-4 border ${expired ? "bg-[#FFF5F4] border-[#F6C9C5]" : "bg-[#FFFBEB] border-[#FDE68A]"}`}>
              <AlertTriangle size={18} className={`shrink-0 mt-0.5 ${expired ? "text-[#EA352B]" : "text-[#B45309]"}`} />
              <p className={`m-0 text-sm ${expired ? "text-[#991B1B]" : "text-[#78350F]"}`}>
                Trade license {expired ? "expired" : "expires"} on <b>{formatDate(data.licenseExpiry)}</b>
                {expired ? " — ask the provider for a renewed license before approving." : " — approval is fine, but it will need renewing soon."}
              </p>
            </div>
          )}

          <DialogSection title="Business details">
            <div className="grid grid-cols-2 sm:grid-cols-1 gap-2">
              <Field Icon={Receipt} label="VAT / TRN number" value={data.vat} copy mono />
              <Field Icon={FileText} label="Trade license no." value={data.license} copy mono />
              <Field Icon={CalendarClock} label="License expiry" value={`${formatDate(data.licenseExpiry)}${expired ? " (expired)" : ""}`} />
              <Field Icon={Building2} label="Emirate" value={data.emirate} />
              <Field Icon={Mail} label="Business email" value={data.email} copy />
              <Field Icon={Phone} label="Phone" value={data.phone} copy />
            </div>
            <div className="mt-2">
              <Field Icon={Clock} label="Operating hours" value={data.hours} />
            </div>
          </DialogSection>

          <DialogSection title="Services requested">
            <div className="flex gap-2 flex-wrap">
              {data.services?.map((s) => (
                <span key={s} className="inline-flex items-center gap-1.5 rounded-full bg-[#F7F7F9] px-3 py-1.5 text-xs font-medium text-[#374151]">
                  <Wrench size={12} className="text-[#EA352B]" /> {s}
                </span>
              ))}
            </div>
          </DialogSection>

          <DialogSection title="Authorized signatory">
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-2xl bg-[#1B1B22] flex items-center justify-center shrink-0">
                <UserSquare2 size={20} className="text-[#fff]" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="m-0 text-sm font-semibold text-[#111]">{data.owner}</p>
                <p className="m-0 text-xs text-[#9CA3AF] truncate">{data.email}</p>
              </div>
              <span className="rounded-full bg-[#F3F3F6] px-3 py-1 text-[11px] font-semibold text-[#374151] uppercase tracking-wide">{data.position}</span>
            </div>
          </DialogSection>

          <DialogSection title="Timeline">
            <ol className="m-0 pl-0 list-none space-y-3">
              <TimelineItem Icon={Send} color="#6B7280" title="Application submitted" time={data.submittedAt} />
              {status === "approved" && <TimelineItem Icon={CheckCircle2} color="#16A34A" title="Approved — provider can create shops" time={data.reviewedAt} />}
              {status === "rejected" && <TimelineItem Icon={XCircle} color="#EA352B" title="Rejected" time={data.reviewedAt} />}
              {status === "pending" && <TimelineItem Icon={Clock} color="#F59E0B" title="Waiting for review" time={null} pending />}
            </ol>
          </DialogSection>
        </div>

        {/* Documents */}
        <div className="min-w-0">
          <DialogSection title={`Verification documents · ${data.documents?.length || 0}`}>
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
              {data.documents?.map((d, i) => (
                <button
                  key={d.key}
                  type="button"
                  onClick={() => setDocIndex(i)}
                  className={`shrink-0 text-left rounded-xl border px-3 py-2 transition ${
                    i === docIndex ? "border-[#EA352B] bg-[#FFF7F6]" : "border-[#EEEEF1] bg-[#fff] hover:border-[#D9DAE0]"
                  }`}
                >
                  <span className={`block text-xs font-semibold ${i === docIndex ? "text-[#EA352B]" : "text-[#111]"}`}>{d.label}</span>
                  <span className="block text-[10px] text-[#9CA3AF]">{d.note}</span>
                </button>
              ))}
            </div>

            <div className="mt-3 relative aspect-[3/4] rounded-2xl overflow-hidden border border-[#EEEEF1] bg-[#F7F7F9]">
              {doc?.url ? (
                <img src={doc.url} alt={doc.label} className="w-full h-full object-contain bg-[#fff]" />
              ) : (
                <DocumentPlaceholder doc={doc} app={data} />
              )}
              {doc?.url && (
                <a
                  href={doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="no-underline absolute top-3 right-3 inline-flex items-center gap-1.5 rounded-lg bg-[#1B1B22cc] backdrop-blur px-3 py-1.5 text-xs font-semibold text-[#fff] hover:bg-[#1B1B22]"
                >
                  Open <ExternalLink size={12} />
                </a>
              )}
            </div>
          </DialogSection>
        </div>
      </div>

      {/* Reject form */}
      {rejecting && (
        <div className="shrink-0 px-6 sm:px-4 py-4 bg-[#FFF7F6] border-t border-[#F6C9C5]">
          <p className="m-0 text-sm font-semibold text-[#111]">Why are you rejecting this application?</p>
          <p className="m-0 mt-0.5 text-xs text-[#6B7280]">The provider will see this reason so they can fix and re-apply.</p>
          <div className="flex gap-2 flex-wrap mt-3">
            {REJECT_REASONS.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setReason(r)}
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${reason === r ? "bg-[#EA352B] text-[#fff]" : "bg-[#fff] border border-[#F6C9C5] text-[#374151] hover:border-[#EA352B]"}`}
              >
                {r}
              </button>
            ))}
          </div>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            autoFocus
            placeholder="Or write your own reason…"
            className="mt-3 w-full rounded-xl bg-[#fff] border border-[#F6C9C5] text-sm text-[#111] placeholder-[#9CA3AF] focus:outline-none focus:border-[#EA352B] focus:ring-0 resize-none"
            style={{ padding: "10px 14px" }}
          />
        </div>
      )}

      {/* Footer */}
      <div className="shrink-0 flex gap-3 flex-wrap justify-end px-6 py-4 sm:px-4 bg-[#fff] border-t border-[#EEEEF1]">
        {rejecting ? (
          <>
            <FooterBtn variant="outline" Icon={X} label="Cancel" onClick={() => (mode === "reject" ? onClose() : setRejecting(false))} />
            <FooterBtn variant="danger" Icon={Ban} label={status === "approved" ? "Revoke approval" : "Reject application"} onClick={submitReject} disabled={!reason.trim()} />
          </>
        ) : status === "pending" ? (
          <>
            <FooterBtn variant="outline" Icon={Ban} label="Reject" onClick={() => setRejecting(true)} />
            <FooterBtn variant="success" Icon={Check} label="Approve provider" onClick={() => onApprove(data.id)} />
          </>
        ) : status === "approved" ? (
          <>
            <span className="mr-auto self-center text-xs text-[#16A34A] inline-flex items-center gap-1.5"><ShieldCheck size={14} /> Verified {fromNow(data.reviewedAt)}</span>
            <FooterBtn variant="outline" Icon={Ban} label="Revoke approval" onClick={() => setRejecting(true)} />
          </>
        ) : (
          <>
            <FooterBtn variant="outline" Icon={X} label="Close" onClick={onClose} />
            <FooterBtn variant="success" Icon={RotateCcw} label="Approve anyway" onClick={() => onApprove(data.id)} />
          </>
        )}
      </div>
    </DialogFrame>
  );
};

function FooterBtn({ Icon, label, onClick, variant, disabled }) {
  const styles = {
    success: "bg-[#16A34A] text-[#fff] hover:bg-[#15803D]",
    danger: "bg-[#EA352B] text-[#fff] hover:bg-[#D42F26]",
    outline: "border border-[#EEEEF1] text-[#374151] hover:border-[#EA352B] hover:text-[#EA352B]",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed sm:flex-1 ${styles[variant]}`}
    >
      <Icon size={15} /> {label}
    </button>
  );
}

function TimelineItem({ Icon, color, title, time, pending }) {
  return (
    <li className="flex gap-3">
      <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${color}1f` }}>
        <Icon size={15} style={{ color }} />
      </span>
      <div>
        <p className="m-0 text-sm font-medium text-[#111]">{title}</p>
        <p className="m-0 text-[11px] text-[#9CA3AF]">{pending ? "Now" : `${formatDate(time, true)} · ${fromNow(time)}`}</p>
      </div>
    </li>
  );
}

// Shown when no file URL is available (sample data): a stylised preview of the document.
function DocumentPlaceholder({ doc, app }) {
  return (
    <div className="absolute inset-0 p-5 flex flex-col">
      <div className="flex-1 rounded-xl bg-[#fff] border border-[#EEEEF1] shadow-sm p-5 flex flex-col">
        <div className="flex items-center justify-between">
          <span className="w-10 h-10 rounded-xl bg-[#FDECEA] flex items-center justify-center"><FileText size={18} className="text-[#EA352B]" /></span>
          <span className="text-[10px] font-semibold uppercase tracking-widest text-[#9CA3AF]">United Arab Emirates</span>
        </div>
        <p className="m-0 mt-4 text-base font-semibold text-[#111]">{doc?.label}</p>
        <p className="m-0 text-xs text-[#9CA3AF]">{doc?.note}</p>
        <div className="mt-5 space-y-2.5">
          <Line label="Business" value={app.title} />
          <Line label="Holder" value={app.owner} />
          {doc?.key === "trade" && <Line label="License no." value={app.license} />}
          {doc?.key === "vat" && <Line label="TRN" value={app.vat} />}
          <Line label="Emirate" value={app.emirate} />
        </div>
        <div className="mt-auto pt-4 space-y-1.5">
          {[80, 64, 72].map((w) => <div key={w} className="h-1.5 rounded-full bg-[#F1F1F4]" style={{ width: `${w}%` }} />)}
        </div>
      </div>
      <p className="m-0 mt-3 text-center text-[11px] text-[#9CA3AF]">
        Sample preview — the uploaded file will show here
      </p>
    </div>
  );
}

function Line({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-dashed border-[#EEEEF1] pb-1.5">
      <span className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">{label}</span>
      <span className="text-xs font-medium text-[#111] truncate">{value}</span>
    </div>
  );
}

export default DocumentModal;
